import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Label } from '../ui/label';
import { 
  Mail, 
  Upload, 
  Send, 
  Trash2, 
  CheckSquare, 
  Square,
  Eye,
  FileText,
  Users,
  AlertCircle,
  Database,
  Play,
  XCircle,
  Settings,
  Plus,
  Edit,
  Loader2
} from 'lucide-react';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Alert, AlertDescription } from '../ui/alert';
import { toast } from 'sonner';
import axios from 'axios';
import { BACKEND_CONFIG } from '../../utils/productionConfig';

const API_URL = BACKEND_CONFIG.apiUrl;

interface EmailRecipient {
  email: string;
  name?: string;
  selected: boolean;
}

interface SMTPAccount {
  _id: string;
  email: string;
  dailyLimit: number;
  todayUsage: number;
  remainingToday: number;
  isActive: boolean;
  priority: number;
  password?: string;
  host?: string;
  port?: number;
}

interface NewSMTPAccount {
  email: string;
  password: string;
  host: string;
  port: number;
  dailyLimit: number;
  priority: number;
}

export default function AdminBulkEmailSender() {
  const [emailInput, setEmailInput] = useState('');
  const [recipients, setRecipients] = useState<EmailRecipient[]>([]);
  const [htmlTemplate, setHtmlTemplate] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [previewEmail, setPreviewEmail] = useState('');
  const [previewRecipient, setPreviewRecipient] = useState<string>('');
  const [showPreview, setShowPreview] = useState(false);
  
  const [smtpAccounts, setSmtpAccounts] = useState<SMTPAccount[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [sendProgress, setSendProgress] = useState({ sent: 0, total: 0, currentAccount: '' });
  
  // SMTP Account Management States
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [addingAccount, setAddingAccount] = useState(false);
  const [newAccount, setNewAccount] = useState<NewSMTPAccount>({
    email: '',
    password: '',
    host: 'smtp.gmail.com',
    port: 587,
    dailyLimit: 500,
    priority: 1
  });
  
  // Edit SMTP Account States
  const [showEditAccount, setShowEditAccount] = useState(false);
  const [editingAccount, setEditingAccount] = useState(false);
  const [editAccount, setEditAccount] = useState<SMTPAccount | null>(null);

  useEffect(() => {
    fetchSmtpAccounts();
  }, []);

  const fetchSmtpAccounts = async () => {
    try {
      setLoadingAccounts(true);
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API_URL}/admin/bulk-sender-smtp`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (data.success) {
        setSmtpAccounts(data.data.accounts);
      }
    } catch (error: any) {
      if (import.meta.env.MODE === 'development') {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch SMTP accounts:', error);
      }
      toast.error(error.response?.data?.message || 'Failed to fetch SMTP accounts');
    } finally {
      setLoadingAccounts(false);
    }
  };

  const handleImportEmails = () => {
    const lines = emailInput.split('\n').filter(line => line.trim());
    const newRecipients: EmailRecipient[] = [];

    lines.forEach(line => {
      const trimmedLine = line.trim();
      const parts = trimmedLine.split('|').map(p => p.trim());
      
      if (parts.length === 1) {
        const email = parts[0];
        if (isValidEmail(email)) {
          newRecipients.push({
            email,
            selected: true
          });
        }
      } else if (parts.length >= 2) {
        const [name, email] = parts;
        if (isValidEmail(email)) {
          newRecipients.push({
            name,
            email,
            selected: true
          });
        }
      }
    });

    if (newRecipients.length > 0) {
      setRecipients([...recipients, ...newRecipients]);
      setEmailInput('');
      toast.success(`Imported ${newRecipients.length} email(s)`);
    } else {
      toast.error('No valid emails found');
    }
  };

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const toggleRecipient = (index: number) => {
    const updated = [...recipients];
    updated[index].selected = !updated[index].selected;
    setRecipients(updated);
  };

  const toggleSelectAll = () => {
    const allSelected = recipients.every(r => r.selected);
    const updated = recipients.map(r => ({ ...r, selected: !allSelected }));
    setRecipients(updated);
  };

  const removeRecipient = (index: number) => {
    const updated = recipients.filter((_, i) => i !== index);
    setRecipients(updated);
  };

  const clearAllRecipients = () => {
    setRecipients([]);
    toast.success('All recipients cleared');
  };

  const handleAddSMTPAccount = async () => {
    if (!newAccount.email || !newAccount.password || !newAccount.host) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!isValidEmail(newAccount.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    try {
      setAddingAccount(true);
      const token = localStorage.getItem('token');
      
      const payload = {
        email: newAccount.email,
        password: newAccount.password,
        smtpHost: newAccount.host,
        smtpPort: newAccount.port,
        dailyLimit: newAccount.dailyLimit,
        priority: newAccount.priority,
      };
      
      const { data } = await axios.post(
        `${API_URL}/admin/bulk-sender-smtp`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (data.success) {
        toast.success('SMTP account added successfully');
        setShowAddAccount(false);
        setNewAccount({
          email: '',
          password: '',
          host: 'smtp.gmail.com',
          port: 587,
          dailyLimit: 500,
          priority: 1
        });
        fetchSmtpAccounts();
      }
    } catch (error: any) {
      if (import.meta.env.MODE === 'development') {
        // eslint-disable-next-line no-console
        console.error('Error adding SMTP account:', error);
      }
      toast.error(error.response?.data?.message || 'Failed to add SMTP account');
    } finally {
      setAddingAccount(false);
    }
  };

  const handleDeleteSMTPAccount = async (accountId: string) => {
    if (!confirm('Are you sure you want to delete this SMTP account?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.delete(
        `${API_URL}/admin/bulk-sender-smtp/${accountId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (data.success) {
        toast.success('SMTP account deleted successfully');
        fetchSmtpAccounts();
      }
    } catch (error: any) {
      if (import.meta.env.MODE === 'development') {
        // eslint-disable-next-line no-console
        console.error('Error deleting SMTP account:', error);
      }
      toast.error(error.response?.data?.message || 'Failed to delete SMTP account');
    }
  };

  const handleToggleSMTPAccount = async (accountId: string, currentStatus: boolean) => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.patch(
        `${API_URL}/admin/bulk-sender-smtp/${accountId}`,
        { isActive: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (data.success) {
        toast.success(`Account ${!currentStatus ? 'activated' : 'deactivated'}`);
        fetchSmtpAccounts();
      }
    } catch (error: any) {
      if (import.meta.env.MODE === 'development') {
        // eslint-disable-next-line no-console
        console.error('Error toggling SMTP account:', error);
      }
      toast.error(error.response?.data?.message || 'Failed to update account');
    }
  };

  const handleEditSMTPAccount = async () => {
    if (!editAccount) return;

    try {
      setEditingAccount(true);
      const token = localStorage.getItem('token');

      const updateData: any = {
        dailyLimit: editAccount.dailyLimit,
        priority: editAccount.priority,
        isActive: editAccount.isActive,
      };

      if (editAccount.password) {
        updateData.password = editAccount.password;
      }

      const { data } = await axios.put(
        `${API_URL}/admin/bulk-sender-smtp/${editAccount._id}`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (data.success) {
        toast.success('SMTP account updated successfully');
        setShowEditAccount(false);
        setEditAccount(null);
        fetchSmtpAccounts();
      }
    } catch (error: any) {
      if (import.meta.env.MODE === 'development') {
        // eslint-disable-next-line no-console
        console.error('Error updating SMTP account:', error);
      }
      toast.error(error.response?.data?.message || 'Failed to update SMTP account');
    } finally {
      setEditingAccount(false);
    }
  };

  const replacePlaceholders = (template: string, recipient: EmailRecipient): string => {
    return template
      .replace(/\{\{name\}\}/g, recipient.name || 'User')
      .replace(/\{\{email\}\}/g, recipient.email);
  };

  const handlePreview = () => {
    const selectedRecipients = recipients.filter(r => r.selected);
    if (selectedRecipients.length === 0) {
      toast.error('Please select at least one recipient');
      return;
    }

    const recipient = selectedRecipients[0];
    const processedHtml = replacePlaceholders(htmlTemplate, recipient);
    setPreviewEmail(processedHtml);
    setPreviewRecipient(recipient.email);
    setShowPreview(true);
  };

  const handleSendEmails = async () => {
    const selectedRecipients = recipients.filter(r => r.selected);

    if (selectedRecipients.length === 0) {
      toast.error('Please select at least one recipient');
      return;
    }

    if (!emailSubject.trim()) {
      toast.error('Please enter an email subject');
      return;
    }

    if (!htmlTemplate.trim()) {
      toast.error('Please enter an email template');
      return;
    }

    if (smtpAccounts.length === 0) {
      toast.error('No active SMTP accounts available. Please add SMTP accounts first.');
      return;
    }

    const availableCapacity = smtpAccounts.reduce((sum, acc) => sum + acc.remainingToday, 0);
    if (availableCapacity < selectedRecipients.length) {
      toast.error(`Not enough email capacity. Can send ${availableCapacity} emails today, but ${selectedRecipients.length} selected.`);
      return;
    }

    setIsSending(true);
    setSendProgress({ sent: 0, total: selectedRecipients.length, currentAccount: '' });

    try {
      const emailData = selectedRecipients.map((recipient) => ({
        to: recipient.email,
        subject: emailSubject,
        html: replacePlaceholders(htmlTemplate, recipient),
      }));

      const token = localStorage.getItem('token');
      const { data } = await axios.post(
        `${API_URL}/admin/bulk-sender-smtp/send-bulk`,
        { emails: emailData },
        { 
          headers: { Authorization: `Bearer ${token}` },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              if (import.meta.env.MODE === 'development') {
                // eslint-disable-next-line no-console
                console.log(`Upload Progress: ${progress}%`);
              }
            }
          }
        }
      );

      if (data.success) {
        setRecipients(recipients.filter(r => !r.selected));
        fetchSmtpAccounts();
        toast.success(`Successfully sent ${data.data.sent} email(s)`, {
          description: data.data.failed > 0 ? `${data.data.failed} email(s) failed` : undefined
        });
      }
    } catch (error: any) {
      if (import.meta.env.MODE === 'development') {
        // eslint-disable-next-line no-console
        console.error('Error sending emails:', error);
      }
      toast.error(error.response?.data?.message || 'Failed to send emails');
    } finally {
      setIsSending(false);
      setSendProgress({ sent: 0, total: 0, currentAccount: '' });
    }
  };

  const selectedCount = recipients.filter(r => r.selected).length;
  const allSelected = recipients.length > 0 && recipients.every(r => r.selected);
  const totalCapacity = smtpAccounts.reduce((sum, acc) => sum + acc.remainingToday, 0);

  return (
    <div className="space-y-6 p-6">
      <div>
        <h2>Bulk Email Sender</h2>
        <p className="text-muted-foreground">
          Send announcement emails to imported email lists with automatic SMTP account rotation
        </p>
      </div>

      <Alert>
        <Database className="h-4 w-4" />
        <AlertDescription>
          <div className="flex items-center justify-between">
            <div>
              <strong>{smtpAccounts.length} SMTP Account(s)</strong> active • 
              <strong className="ml-2">{totalCapacity} emails</strong> remaining today
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="default"
                onClick={() => setShowAddAccount(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Account
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={fetchSmtpAccounts}
                disabled={loadingAccounts}
              >
                <Settings className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </AlertDescription>
      </Alert>

      {smtpAccounts.length === 0 && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            No active SMTP accounts found. Please add SMTP accounts in the SMTP Accounts section first.
          </AlertDescription>
        </Alert>
      )}

      <Tabs defaultValue="recipients" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="smtp-accounts">
            <Settings className="w-4 h-4 mr-2" />
            SMTP Accounts ({smtpAccounts.length})
          </TabsTrigger>
          <TabsTrigger value="recipients">
            <Users className="w-4 h-4 mr-2" />
            Recipients ({recipients.length})
          </TabsTrigger>
          <TabsTrigger value="template">
            <FileText className="w-4 h-4 mr-2" />
            Email Template
          </TabsTrigger>
          <TabsTrigger value="send">
            <Send className="w-4 h-4 mr-2" />
            Review & Send
          </TabsTrigger>
        </TabsList>

        <TabsContent value="smtp-accounts" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>SMTP Account Management</CardTitle>
                  <CardDescription>
                    Manage your bulk sender email accounts
                  </CardDescription>
                </div>
                <Button onClick={() => setShowAddAccount(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Account
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {smtpAccounts.length === 0 ? (
                <div className="text-center py-12">
                  <Mail className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg mb-2">No SMTP Accounts</h3>
                  <p className="text-muted-foreground mb-4">
                    Add your first SMTP account to start sending bulk emails
                  </p>
                  <Button onClick={() => setShowAddAccount(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add SMTP Account
                  </Button>
                </div>
              ) : (
                <ScrollArea className="h-[500px]">
                  <div className="space-y-3">
                    {smtpAccounts.map((account) => (
                      <div
                        key={account._id}
                        className="p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <Mail className="w-5 h-5 text-muted-foreground" />
                              <span className="font-medium">{account.email}</span>
                              <Badge variant={account.isActive ? "default" : "secondary"}>
                                {account.isActive ? 'Active' : 'Inactive'}
                              </Badge>
                              <Badge variant="outline" className="text-xs">
                                Priority {account.priority}
                              </Badge>
                            </div>
                            <div className="grid grid-cols-3 gap-4 mt-3 text-sm">
                              <div>
                                <span className="text-muted-foreground">Daily Limit:</span>
                                <div className="font-medium">{account.dailyLimit} emails</div>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Today's Usage:</span>
                                <div className="font-medium">{account.todayUsage || 0} emails</div>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Remaining:</span>
                                <div className="font-medium text-green-600">
                                  {account.remainingToday} emails
                                </div>
                              </div>
                            </div>
                            <div className="mt-3">
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div
                                  className="bg-rose-500 h-2 rounded-full transition-all"
                                  style={{
                                    width: `${((account.todayUsage || 0) / account.dailyLimit) * 100}%`
                                  }}
                                ></div>
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-2 ml-4">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setEditAccount(account);
                                setShowEditAccount(true);
                              }}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant={account.isActive ? "secondary" : "default"}
                              onClick={() => handleToggleSMTPAccount(account._id, account.isActive)}
                            >
                              {account.isActive ? 'Deactivate' : 'Activate'}
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDeleteSMTPAccount(account._id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="recipients" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Import Email Recipients</CardTitle>
              <CardDescription>
                Paste email addresses (one per line). Format: email or name|email
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email-input">Email List</Label>
                <Textarea
                  id="email-input"
                  placeholder="user@example.com
John Doe|john@example.com
Jane Smith|jane@example.com"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  rows={8}
                />
              </div>
              <Button onClick={handleImportEmails} disabled={!emailInput.trim()}>
                <Upload className="w-4 h-4 mr-2" />
                Import Emails
              </Button>
            </CardContent>
          </Card>

          {recipients.length > 0 && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Email Recipients</CardTitle>
                    <CardDescription>
                      {selectedCount} of {recipients.length} selected
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={toggleSelectAll}
                    >
                      {allSelected ? (
                        <>
                          <Square className="w-4 h-4 mr-2" />
                          Deselect All
                        </>
                      ) : (
                        <>
                          <CheckSquare className="w-4 h-4 mr-2" />
                          Select All
                        </>
                      )}
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={clearAllRecipients}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Clear All
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[450px] rounded-md border">
                  <div className="p-4 space-y-2">
                    {recipients.map((recipient, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent transition-colors"
                      >
                        <div className="flex items-center gap-3 flex-1">
                          <input
                            type="checkbox"
                            checked={recipient.selected}
                            onChange={() => toggleRecipient(index)}
                            className="w-4 h-4"
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <Mail className="w-4 h-4 text-muted-foreground" />
                              <span className="text-sm">{recipient.email}</span>
                            </div>
                            {recipient.name && (
                              <Badge variant="secondary" className="text-xs mt-1">
                                {recipient.name}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeRecipient(index)}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="template" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Email Template</CardTitle>
              <CardDescription>
                Create your HTML email template. Use placeholders to insert dynamic values.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="subject">Email Subject</Label>
                <Input
                  id="subject"
                  placeholder="Enter email subject"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="html-template">HTML Template</Label>
                <Textarea
                  id="html-template"
                  placeholder={`<html>
  <body style="font-family: Arial, sans-serif; padding: 20px;">
    <h1>Hello {{name}}!</h1>
    <p>Your email: {{email}}</p>
    <p>This is an announcement from Atootbandan.</p>
    <p>Thank you!</p>
  </body>
</html>`}
                  value={htmlTemplate}
                  onChange={(e) => setHtmlTemplate(e.target.value)}
                  rows={16}
                  className="font-mono text-sm"
                />
              </div>

              <Alert>
                <AlertDescription>
                  <strong>Available Placeholders:</strong>
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>
                      <code className="bg-muted px-1 rounded text-xs">{'{{name}}'}</code> - Recipient name (defaults to "User")
                    </li>
                    <li>
                      <code className="bg-muted px-1 rounded text-xs">{'{{email}}'}</code> - Recipient email address
                    </li>
                  </ul>
                </AlertDescription>
              </Alert>

              <Button onClick={handlePreview} disabled={!htmlTemplate.trim() || selectedCount === 0}>
                <Eye className="w-4 h-4 mr-2" />
                Preview Email
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="send" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Review & Send Campaign</CardTitle>
              <CardDescription>
                Review your campaign details before sending
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-lg border bg-card">
                  <div className="text-sm text-muted-foreground mb-1">Total Recipients</div>
                  <div className="text-3xl">{recipients.length}</div>
                </div>
                <div className="p-4 rounded-lg border bg-card">
                  <div className="text-sm text-muted-foreground mb-1">Selected to Send</div>
                  <div className="text-3xl">{selectedCount}</div>
                </div>
              </div>

              {emailSubject && (
                <div className="p-4 rounded-lg border bg-card">
                  <div className="text-sm text-muted-foreground mb-2">Email Subject</div>
                  <div>{emailSubject}</div>
                </div>
              )}

              {smtpAccounts.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm">SMTP Accounts Status</h4>
                  <div className="space-y-2">
                    {smtpAccounts.map((account) => (
                      <div key={account._id} className="flex items-center justify-between p-3 rounded-lg border bg-card">
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm">{account.email}</span>
                          <Badge variant="outline" className="text-xs">
                            Priority {account.priority}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={account.remainingToday > 0 ? "default" : "destructive"} className="text-xs">
                            {account.remainingToday} / {account.dailyLimit} remaining
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Alert>
                    <AlertDescription className="text-xs">
                      <strong>Note:</strong> Emails will be sent using these accounts automatically. The system will rotate to the next account when one reaches its daily limit.
                    </AlertDescription>
                  </Alert>
                </div>
              )}

              {isSending && (
                <div className="p-4 rounded-lg border bg-card">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-rose-500"></div>
                    <span className="text-sm">Sending emails...</span>
                  </div>
                  {sendProgress.total > 0 && (
                    <div className="space-y-2">
                      <div className="text-sm text-muted-foreground">
                        {sendProgress.sent} / {sendProgress.total} sent
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-rose-500 h-2 rounded-full transition-all"
                          style={{ width: `${(sendProgress.sent / sendProgress.total) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <Button 
                size="lg" 
                className="w-full"
                disabled={isSending || selectedCount === 0 || !htmlTemplate || !emailSubject || smtpAccounts.length === 0}
                onClick={handleSendEmails}
              >
                {isSending ? (
                  <>
                    <Play className="w-4 h-4 mr-2 animate-pulse" />
                    Sending Emails...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send {selectedCount} Email(s)
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {showPreview && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <div>
                <h3 className="text-xl">Email Preview</h3>
                <p className="text-sm text-muted-foreground">Preview for: {previewRecipient}</p>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setShowPreview(false)}>
                <XCircle className="w-5 h-5" />
              </Button>
            </div>
            <div className="p-6">
              <div className="mb-4 p-3 bg-muted rounded-lg">
                <div className="text-sm text-muted-foreground mb-1">Subject:</div>
                <div>{emailSubject}</div>
              </div>
              <div className="border rounded-lg p-4 bg-white">
                <div dangerouslySetInnerHTML={{ __html: previewEmail }} />
              </div>
            </div>
          </div>
        </div>
      )}

      {showAddAccount && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center rounded-t-xl">
              <div>
                <h3 className="text-xl">Add SMTP Account</h3>
                <p className="text-sm text-muted-foreground">Configure a new email account for sending</p>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setShowAddAccount(false)}>
                <XCircle className="w-5 h-5" />
              </Button>
            </div>
            <div className="p-6 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="smtp-email">Email Address *</Label>
                <Input
                  id="smtp-email"
                  type="email"
                  placeholder="your-email@gmail.com"
                  value={newAccount.email}
                  onChange={(e) => setNewAccount({ ...newAccount, email: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="smtp-password">App Password *</Label>
                <Input
                  id="smtp-password"
                  type="password"
                  placeholder="Enter app password"
                  value={newAccount.password}
                  onChange={(e) => setNewAccount({ ...newAccount, password: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">
                  For Gmail: Use an App Password, not your regular password. 
                  <a 
                    href="https://myaccount.google.com/apppasswords" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-rose-500 hover:underline ml-1"
                  >
                    Generate App Password
                  </a>
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="smtp-host">SMTP Host *</Label>
                  <Input
                    id="smtp-host"
                    placeholder="smtp.gmail.com"
                    value={newAccount.host}
                    onChange={(e) => setNewAccount({ ...newAccount, host: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="smtp-port">Port *</Label>
                  <Input
                    id="smtp-port"
                    type="number"
                    placeholder="587"
                    value={newAccount.port}
                    onChange={(e) => setNewAccount({ ...newAccount, port: parseInt(e.target.value) || 587 })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="smtp-limit">Daily Email Limit *</Label>
                  <Input
                    id="smtp-limit"
                    type="number"
                    placeholder="500"
                    value={newAccount.dailyLimit}
                    onChange={(e) => setNewAccount({ ...newAccount, dailyLimit: parseInt(e.target.value) || 500 })}
                  />
                  <p className="text-xs text-muted-foreground">
                    Gmail free: 500/day, Workspace: 2000/day
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="smtp-priority">Priority</Label>
                  <Input
                    id="smtp-priority"
                    type="number"
                    placeholder="1"
                    value={newAccount.priority}
                    onChange={(e) => setNewAccount({ ...newAccount, priority: parseInt(e.target.value) || 1 })}
                  />
                  <p className="text-xs text-muted-foreground">
                    Lower number = higher priority
                  </p>
                </div>
              </div>

              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-xs">
                  <strong>Important:</strong> The system will automatically switch between accounts based on priority and daily limits.
                  Make sure to use App Passwords for Gmail accounts for security.
                </AlertDescription>
              </Alert>

              <div className="flex gap-3 pt-4">
                <Button
                  className="flex-1"
                  onClick={handleAddSMTPAccount}
                  disabled={addingAccount}
                >
                  {addingAccount ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Adding Account...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Add SMTP Account
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowAddAccount(false)}
                  disabled={addingAccount}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showEditAccount && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center rounded-t-xl">
              <div>
                <h3 className="text-xl">Edit SMTP Account</h3>
                <p className="text-sm text-muted-foreground">Update the email account settings</p>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setShowEditAccount(false)}>
                <XCircle className="w-5 h-5" />
              </Button>
            </div>
            <div className="p-6 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="smtp-email">Email Address *</Label>
                <Input
                  id="smtp-email"
                  type="email"
                  placeholder="your-email@gmail.com"
                  value={editAccount?.email || ''}
                  onChange={(e) => setEditAccount({ ...editAccount!, email: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="smtp-password">App Password *</Label>
                <Input
                  id="smtp-password"
                  type="password"
                  placeholder="Enter app password"
                  value={editAccount?.password || ''}
                  onChange={(e) => setEditAccount({ ...editAccount!, password: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">
                  For Gmail: Use an App Password, not your regular password. 
                  <a 
                    href="https://myaccount.google.com/apppasswords" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-rose-500 hover:underline ml-1"
                  >
                    Generate App Password
                  </a>
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="smtp-host">SMTP Host *</Label>
                  <Input
                    id="smtp-host"
                    placeholder="smtp.gmail.com"
                    value={editAccount?.host || ''}
                    onChange={(e) => setEditAccount({ ...editAccount!, host: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="smtp-port">Port *</Label>
                  <Input
                    id="smtp-port"
                    type="number"
                    placeholder="587"
                    value={editAccount?.port || 587}
                    onChange={(e) => setEditAccount({ ...editAccount!, port: parseInt(e.target.value) || 587 })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="smtp-limit">Daily Email Limit *</Label>
                  <Input
                    id="smtp-limit"
                    type="number"
                    placeholder="500"
                    value={editAccount?.dailyLimit || 500}
                    onChange={(e) => setEditAccount({ ...editAccount!, dailyLimit: parseInt(e.target.value) || 500 })}
                  />
                  <p className="text-xs text-muted-foreground">
                    Gmail free: 500/day, Workspace: 2000/day
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="smtp-priority">Priority</Label>
                  <Input
                    id="smtp-priority"
                    type="number"
                    placeholder="1"
                    value={editAccount?.priority || 1}
                    onChange={(e) => setEditAccount({ ...editAccount!, priority: parseInt(e.target.value) || 1 })}
                  />
                  <p className="text-xs text-muted-foreground">
                    Lower number = higher priority
                  </p>
                </div>
              </div>

              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-xs">
                  <strong>Important:</strong> The system will automatically switch between accounts based on priority and daily limits.
                  Make sure to use App Passwords for Gmail accounts for security.
                </AlertDescription>
              </Alert>

              <div className="flex gap-3 pt-4">
                <Button
                  className="flex-1"
                  onClick={handleEditSMTPAccount}
                  disabled={editingAccount}
                >
                  {editingAccount ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Updating Account...
                    </>
                  ) : (
                    <>
                      <Edit className="w-4 h-4 mr-2" />
                      Update SMTP Account
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowEditAccount(false)}
                  disabled={editingAccount}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}